import {
  BannerType,
  ContentFulScreens,
  IActionNavigation,
  ICustomizedBanner,
  ICustomizedBannerAction,
  NavigationType,
  setIsOverlayLogin,
  Shape,
  useAppDispatch,
} from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import {
  ActionPlacement,
  BannerActionType,
  BannerContentProps,
  BannerImageDirection,
  ContentPlacement,
  Platform,
  Shape as ButtonShape,
} from "@next-platform/client-common-spicy-ui";

import {
  bannerVariantMap,
  CONTENTFUL_SCREENS,
  DYNAMIC_ROUTE_SCREEN_MAP,
  ROUTE_CONTENTFUL_SCREEN_MAP,
  ROUTES,
  staticRoutesExcludedForLayoutBanner,
} from "@/constants";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";

import { IBannerProp, ICmsBannerService } from "./types";

const cmsBannerServiceLogger = appLogger.withTag("CmsBannerService");

class CmsBannerService {
  private banners: ICustomizedBanner[];
  private dispatch: ReturnType<typeof useAppDispatch>;
  private navigate: ReturnType<typeof useLocalizedNavigation>;
  private onCTAClick: (banner: ICustomizedBanner, action: ICustomizedBannerAction, screen: string) => void;
  public customizedBanners: BannerContentProps[] = [];
  public static pathname: string | undefined;
  public static contentfulScreen: ContentFulScreens | undefined;
  private onPopUpCTAClick: () => Promise<void>;
  private screen: ContentFulScreens | undefined;
  constructor({ banners, dispatch, navigate, onCTAClick, onPopUpCTAClick, screen }: ICmsBannerService) {
    this.banners = banners || [];
    this.screen = screen;
    this.dispatch = dispatch;
    this.navigate = navigate;
    this.onCTAClick = onCTAClick;
    this.onPopUpCTAClick = onPopUpCTAClick;
    this.customizeBanners();
  }
  private customizeBanners = () => {
    if (!this.banners.length) {
      return;
    }
    this.customizedBanners = this.banners?.map((banner) => this.mapBannerToProps(banner));
  };
  private mapBannerToProps = (banner: ICustomizedBanner): IBannerProp => {
    const hasActions = banner?.actions && banner.actions.length > 0;
    const hasBackgroundAction = !!banner?.backgroundAction;

    let actions;
    let onBannerClick;

    if (hasActions) {
      actions = banner?.actions?.map((action) => this.mapActionToProps(banner, action));
    } else if (hasBackgroundAction) {
      onBannerClick = this.handleBannerClick(banner);
    }

    return {
      name: banner.name,
      variant: bannerVariantMap[banner?.placement as unknown as BannerType],
      backgroundImageUrl: banner?.backgroundImageUrl as string,
      backgroundColor: banner?.backgroundColor as string,
      contentPlacement: banner?.contentPlacement as unknown as ContentPlacement, // TODO(web-app): [NX-3574] update to not cast unknown
      additionalImage: {
        url: banner?.additionalImage?.url as string,
        direction: banner?.additionalImage?.placement as BannerImageDirection,
      },
      actions,
      onBannerClick,
      title: {
        label: banner?.title?.label,
        fontSize: banner?.title?.fontSize,
        color: banner?.title?.color,
        fontWeight: banner?.title?.fontWeight,
      },
      description: {
        label: banner?.description?.label,
        fontSize: banner?.description?.fontSize,
        color: banner?.description?.color,
        fontWeight: banner?.description?.fontWeight,
      },
      platforms: (banner?.platforms as unknown as Platform[]) || [], // TODO(web-app): [NX-3574] update this to not cast unknown
      closable: banner?.closable,
      transitionTime: banner?.transitionTime,
    };
  };

  private runCTA = async (banner: ICustomizedBanner, action: ICustomizedBannerAction, navigation: IActionNavigation) => {
    try {
      await this.onPopUpCTAClick();
    } catch (closeBannerError) {
      cmsBannerServiceLogger.error("Failed to close the pop-up banner before running the CTA", { banner: banner.name }, closeBannerError);
    }
    this.onCTAClick(banner, action, this.screen ?? CmsBannerService.contentfulScreen ?? "");
    this.actionOnPress(navigation)();
  };

  private handleBannerClick = (banner: ICustomizedBanner) => {
    return () => {
      if (!banner.backgroundAction) {
        return;
      }
      void this.runCTA(banner, { label: "Background" } as unknown as ICustomizedBannerAction, banner.backgroundAction);
    };
  };

  private mapActionToProps = (banner: ICustomizedBanner, action: ICustomizedBannerAction): BannerActionType => {
    return {
      label: action?.label,
      fontSize: action?.fontSize,
      fontWeight: action?.fontWeight,
      color: action?.color,
      shape: action?.shape === Shape.Square ? ButtonShape.SQUARE : ButtonShape.PILL,
      backgroundColor: action?.backgroundColor as string,
      hoverBackgroundColor: action?.hoverBackgroundColor as string,
      hoverColor: action?.labelHoverColor as string,
      placement: action?.placement as unknown as ActionPlacement, // TODO(web-app): [NX-3574] update this to not cast undefined
      onClick: this.handleOnPress(banner, action),
    };
  };

  private handleOnPress = (banner: ICustomizedBanner, action: ICustomizedBannerAction) => {
    return () => {
      void this.runCTA(banner, action, action.navigation);
    };
  };

  private actionOnPress = (navigation: IActionNavigation) => {
    if (!navigation) {
      return () => {};
    }
    const { type: navigationType, destination } = navigation;
    const isDestinationRegister = destination?.toLowerCase() === CONTENTFUL_SCREENS.REGISTER?.toLowerCase();
    if (isDestinationRegister) {
      return () => this.dispatch(setIsOverlayLogin(true));
    }
    if (navigationType === NavigationType.EXTERNAL) {
      return () => window.open(destination, "_blank", "noopener");
    }
    if (navigationType === NavigationType.INTERNAL) {
      const [path, query] = (destination ?? "").split("?");
      const normalizedPath = path || "/";
      const route = query?.length ? `${normalizedPath}?${query}` : normalizedPath;
      return () => this.navigate(route);
    }
    return () => {};
  };

  public static getContentfulScreen = (): ContentFulScreens | undefined => {
    if (!CmsBannerService.pathname) {
      return;
    }
    const contentFulScreen = ROUTE_CONTENTFUL_SCREEN_MAP[CmsBannerService.pathname as keyof typeof ROUTE_CONTENTFUL_SCREEN_MAP];
    if (contentFulScreen) {
      return contentFulScreen as ContentFulScreens;
    }
    const contentfulRouteKeyPair = Object.entries(DYNAMIC_ROUTE_SCREEN_MAP).find(([key]) => CmsBannerService.pathname?.includes(key));
    if (!contentfulRouteKeyPair) {
      return undefined;
    }
    const [, contentfulScreenKey] = contentfulRouteKeyPair;
    return contentfulScreenKey as unknown as ContentFulScreens;
  };

  public static setPathname(pathname: string) {
    if (!pathname || CmsBannerService.pathname === pathname) {
      return;
    }
    CmsBannerService.pathname = pathname;
    this.contentfulScreen = CmsBannerService.getContentfulScreen();
  }

  public static isPageExcludedForLayoutBanner = (pathname: string) => {
    // The routes /creator/leaderboard and /creator/xyz both start with /creator, causing a conflict in the includes condition. This results in the wrong banner being displayed on each page (the leaderboard banner on the creator page and vice versa).
    if (pathname === ROUTES.leaderboards) {
      return false;
    }
    if ((staticRoutesExcludedForLayoutBanner as string[]).includes(pathname)) {
      return true;
    }
    return pathname.includes(ROUTES.creator);
  };
}

export default CmsBannerService;
